(() => {
  'use strict';

  const ROOT_SELECTOR = '#CartIncentive';
  let initTimer = null;

  function getRoot() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function showError(root, message) {
    const error = root && root.querySelector('.cart-incentive__error');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  function clearError(root) {
    const error = root && root.querySelector('.cart-incentive__error');
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
  }

  function scoreItem(item, context) {
    const role = (item.dataset.role || '').toLowerCase();
    const scene = (item.dataset.scene || '').toLowerCase();
    const group = (item.dataset.group || '').toLowerCase();
    const occasion = (item.dataset.occasion || '').toLowerCase();
    const price = Number(item.dataset.price) || 0;

    let score = 0;

    // Relevance first: manual group and scene are the strongest signals.
    if (context.group && group && group === context.group) score += 50;
    if (context.scene && scene && scene === context.scene) score += 40;

    // Shopping role signals.
    if (role === 'complementary') score += 30;
    if (role === 'consumable') score += 20;
    if (role === 'impulse') score += 15;

    // Occasion match (fallback relevance).
    if (context.occasion && occasion && occasion === context.occasion) score += 20;

    // Price fit is a secondary signal only. There is intentionally no
    // minimum price filter: low-price items stay eligible.
    let fit = 10;
    if (!context.unlocked && context.remaining > 0) {
      const diff = Math.abs(price - context.remaining) / 100;
      fit = Math.max(0, Math.round(30 - Math.min(30, diff * 2)));
    }
    score += fit;

    return score;
  }

  function init() {
    const root = getRoot();
    if (!root) return;

    const groupWraps = Array.from(root.querySelectorAll('[data-group-wrap]'));
    groupWraps.forEach((wrap) => {
      wrap.hidden = true;
      const list = wrap.querySelector('[data-list]');
      if (list) list.innerHTML = '';
    });

    const pool = root.querySelector('#CartIncentive-Pool');
    if (!pool) return;

    const rows = Array.from(pool.querySelectorAll('.cart-incentive__item'));
    if (!rows.length) return;

    const maxSetting = parseInt(root.dataset.maxRecommendations || '4', 10) || 4;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const maxCount = Math.max(1, Math.min(isMobile ? 3 : maxSetting, maxSetting));

    const context = {
      scene: (root.dataset.scene || '').toLowerCase(),
      group: (root.dataset.group || '').toLowerCase(),
      occasion: (root.dataset.occasion || '').toLowerCase(),
      unlocked: root.dataset.unlocked === 'true',
      remaining: Number(root.dataset.remainingCents) || 0,
    };

        // Keep free-shipping progress in sync after every drawer refresh.
    const threshold = Number(root.dataset.thresholdCents) || 0;
    const cartCents = Number(root.dataset.cartCents) || 0;
    const unlocked = root.dataset.unlocked === 'true';
    let pct = 0;
    if (threshold > 0) {
      pct = Math.min(100, Math.round((cartCents / threshold) * 100));
    } else if (unlocked) {
      pct = 100;
    }
    if (unlocked) pct = 100;
    const fill = root.querySelector('.cart-incentive__bar-fill');
    const truck = root.querySelector('.cart-incentive__truck');
    const bar = root.querySelector('.cart-incentive__bar');
    if (fill) fill.style.width = pct + '%';
    if (truck) truck.style.left = Math.min(96, Math.max(4, pct)) + '%';
    if (bar) bar.setAttribute('aria-valuenow', pct);

    const ranked = rows
      .map((item) => ({ item, score: scoreItem(item, context) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, maxCount);

    const selectedByPool = { complete: [], addons: [] };
    ranked.forEach(({ item }) => {
      const poolName = item.dataset.pool === 'addons' ? 'addons' : 'complete';
      selectedByPool[poolName].push(item);
    });

    Object.keys(selectedByPool).forEach((poolName) => {
      const wrap = root.querySelector(`[data-group-wrap="${poolName}"]`);
      const items = selectedByPool[poolName];
      if (!wrap || !items.length) return;
      const list = wrap.querySelector('[data-list]');
      items.forEach((item) => list.appendChild(item));
      wrap.hidden = false;
    });

    // Remove rows that were not selected to keep the drawer lightweight.
    pool.querySelectorAll('.cart-incentive__item').forEach((item) => item.remove());
  }

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-incentive-add]');
    if (!button) return;

    event.preventDefault();
    const root = button.closest(ROOT_SELECTOR);
    const textElement = button.querySelector('.cart-incentive__add-text');
    const originalText = textElement ? textElement.textContent : 'Add';

    clearError(root);
    button.disabled = true;
    if (textElement) textElement.textContent = 'Adding…';

    const formData = new FormData();
    formData.append('id', button.dataset.variantId);
    formData.append('quantity', '1');
    formData.append('sections', ['cart-drawer', 'cart-icon-bubble'].join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || data.message || 'Unable to add this item.');
      }

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
      } else {
        window.location = window.routes.cart_url;
      }
    } catch (error) {
      showError(root, error.message || 'Something went wrong. Please try again.');
      button.disabled = false;
      if (textElement) textElement.textContent = originalText;
    }
  });

  // Re-rank after the drawer is re-rendered (add, quantity change or removal).
  const scheduleInit = () => {
    clearTimeout(initTimer);
    initTimer = setTimeout(init, 80);
  };

  new MutationObserver((mutations) => {
    const shouldInit = mutations.some((mutation) =>
      Array.from(mutation.addedNodes || []).some((node) => {
        if (node.nodeType !== 1) return false;
        return node.id === 'CartIncentive' || (node.querySelector && node.querySelector(ROOT_SELECTOR));
      })
    );
    if (shouldInit) scheduleInit();
  }).observe(document.body, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
